import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Badge,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Dashboard,
  Inventory,
  Category,
  Settings,
  Feedback,
  Home,
  ViewInAr,
  Apps,
  TuneOutlined,
  ChatBubbleOutline,
  Business,
} from "@mui/icons-material";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  description?: string;
  color?: string;
}

const mainNavigationItems: NavItem[] = [
  {
    label: "Dashboard",
    path: "/",
    icon: <Home />,
    description: "Overview & Analytics",
    color: "#1976d2",
  },
  {
    label: "Inventory",
    path: "/inventory",
    icon: <ViewInAr />,
    description: "Manage Items",
    color: "#2e7d32",
  },
  {
    label: "Categories",
    path: "/categories",
    icon: <Category />,
    description: "Organize & Filter",
    color: "#ed6c02",
  },
  {
    label: "Merchants",
    path: "/merchants",
    icon: <Business />,
    description: "Manage Vendors",
    color: "#d32f2f",
  },
  {
    label: "Feedback",
    path: "/request-improvement",
    icon: <ChatBubbleOutline />,
    description: "Share Ideas",
    color: "#9c27b0",
  },
];

const settingsItem: NavItem = {
  label: "Settings",
  path: "/settings",
  icon: <TuneOutlined />,
  description: "Configure App",
  color: "#757575",
};

interface NavigationProps {
  onNavigate?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleNavigate = (path: string) => {
    navigate(path);
    // Close mobile drawer after navigation
    if (isMobile && onNavigate) {
      onNavigate();
    }
  };

  const renderNavItem = (item: NavItem) => {
    const isSelected = location.pathname === item.path;

    if (isMobile) {
      // Mobile version with enhanced styling
      return (
        <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
          {" "}
          {/* Reduced from 0.5 */}
          <ListItemButton
            selected={isSelected}
            onClick={() => handleNavigate(item.path)}
            sx={{
              minHeight: 44, // Reduced from 56
              borderRadius: 2,
              mx: 1,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              "&.Mui-selected": {
                backgroundColor: `${item.color}15`,
                borderLeft: `4px solid ${item.color}`,
                transform: "translateX(4px)",
                "&:hover": {
                  backgroundColor: `${item.color}20`,
                },
                "& .MuiListItemIcon-root": {
                  color: item.color,
                  transform: "scale(1.1)",
                },
                "& .MuiListItemText-primary": {
                  fontWeight: 600,
                  color: item.color,
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: "4px",
                  backgroundColor: item.color,
                  borderRadius: "0 4px 4px 0",
                },
              },
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(240, 246, 252, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
                transform: "translateX(2px)",
                "& .MuiListItemIcon-root": {
                  transform: "scale(1.05)",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40, // Reduced from 48
                transition: "all 0.3s ease",
                "& .MuiSvgIcon-root": {
                  fontSize: "1.2rem", // Reduced from 1.4rem
                },
              }}
            >
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.description}
              primaryTypographyProps={{
                fontSize: "0.875rem", // Reduced from 0.95rem
                fontWeight: isSelected ? 600 : 500,
              }}
              secondaryTypographyProps={{
                fontSize: "0.7rem", // Reduced from 0.75rem
                color: "text.secondary",
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    }

    // Desktop version with sophisticated hover effects
    return (
      <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
        <Tooltip
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {item.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.description}
              </Typography>
            </Box>
          }
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(22, 27, 34, 0.95)"
                    : "rgba(0, 0, 0, 0.9)",
                backdropFilter: "blur(10px)",
                border: (theme) =>
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(240, 246, 252, 0.1)"
                    : "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: 2,
                boxShadow: (theme) =>
                  theme.palette.mode === "dark"
                    ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                color: (theme) =>
                  theme.palette.mode === "dark" ? "white" : "white",
              },
            },
            arrow: {
              sx: {
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(22, 27, 34, 0.95)"
                    : "rgba(0, 0, 0, 0.9)",
              },
            },
          }}
        >
          <ListItemButton
            selected={isSelected}
            onClick={() => handleNavigate(item.path)}
            sx={{
              minHeight: 52,
              justifyContent: "center",
              borderRadius: 2.5,
              mx: 0.5,
              position: "relative",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              "&.Mui-selected": {
                backgroundColor: `${item.color}15`,
                transform: "scale(1.05)",
                boxShadow: `0 4px 20px ${item.color}25`,
                "&:hover": {
                  backgroundColor: `${item.color}20`,
                  transform: "scale(1.08)",
                },
                "& .MuiListItemIcon-root": {
                  color: item.color,
                  transform: "scale(1.2)",
                },
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: -8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 4,
                  height: "60%",
                  backgroundColor: item.color,
                  borderRadius: "0 4px 4px 0",
                  boxShadow: `2px 0 8px ${item.color}40`,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: 2.5,
                  border: `2px solid ${item.color}30`,
                  animation: "pulse 2s infinite",
                },
              },
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(240, 246, 252, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
                transform: "scale(1.03)",
                "& .MuiListItemIcon-root": {
                  transform: "scale(1.1)",
                  color: item.color,
                },
              },
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.5 },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: "center",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                },
              }}
            >
              {item.badge ? (
                <Badge
                  badgeContent={item.badge}
                  color="error"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.6rem",
                      minWidth: 16,
                      height: 16,
                      borderRadius: "50%",
                    },
                  }}
                >
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </ListItem>
    );
  };

  if (isMobile) {
    // Mobile version with brand header
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(22, 27, 34, 0.95) 0%, rgba(30, 36, 47, 0.95) 100%)"
              : "linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%)",
        }}
      >
        {/* Brand Header - More compact */}
        <Box
          sx={{
            p: 1.5, // Reduced from 3 to 1.5
            borderBottom: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(240, 246, 252, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.08)",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0A84FF 0%, #1565c0 100%)"
                : "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {" "}
            {/* Reduced gap from 2 to 1.5 */}
            <Avatar
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.2)",
                width: 32, // Reduced from 40
                height: 32, // Reduced from 40
                backdropFilter: "blur(10px)",
              }}
            >
              <ViewInAr sx={{ fontSize: "1.2rem" }} /> {/* Reduced icon size */}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ fontSize: "1rem" }} // Reduced from 1.1rem
              >
                Real Estate Catalog
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.8, fontSize: "0.7rem" }} // Reduced from 0.75rem
              >
                Professional Inventory
              </Typography>
            </Box>
          </Box>
        </Box>
        <List sx={{ flex: 1, py: 1.5 }}>
          {" "}
          {/* Reduced py from 2 to 1.5 */}
          {mainNavigationItems.map((item) => renderNavItem(item))}
        </List>
        <Divider sx={{ mx: 2, opacity: 0.3 }} />
        <List sx={{ pb: 1.5 }}>{renderNavItem(settingsItem)}</List>{" "}
        {/* Reduced pb from 2 to 1.5 */}
      </Box>
    );
  }

  // Desktop version with sophisticated glass morphism effect
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: 80,
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(22, 27, 34, 0.95) 0%, rgba(30, 36, 47, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
        backdropFilter: "blur(20px)",
        borderRight: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(240, 246, 252, 0.1)"
            : "1px solid rgba(255, 255, 255, 0.2)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(10, 132, 255, 0.05) 0%, rgba(10, 132, 255, 0.02) 100%)"
              : "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
          borderRadius: "inherit",
          pointerEvents: "none",
          zIndex: 1,
        },
      }}
    >
      {/* Logo/Brand section */}
      <Box
        sx={{
          py: 3,
          display: "flex",
          justifyContent: "center",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "primary.main",
            width: 44,
            height: 44,
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            boxShadow: "0 4px 20px rgba(25, 118, 210, 0.3)",
            border: "2px solid rgba(255, 255, 255, 0.3)",
          }}
        >
          <ViewInAr sx={{ fontSize: "1.4rem" }} />
        </Avatar>
      </Box>

      <List sx={{ flex: 1, py: 2, px: 1 }}>
        {mainNavigationItems.map((item) => renderNavItem(item))}
      </List>

      <Divider sx={{ mx: 2, opacity: 0.2 }} />

      <List sx={{ pb: 2, px: 1 }}>{renderNavItem(settingsItem)}</List>
    </Box>
  );
};

export default Navigation;
